import { Card, CheckBox, Explination, Help, Input, Select, Submit, Table, ViewHeader, notify, useGet, usePost, useSubmit } from '@enkla-gui'
import { useHistory, useParams } from 'react-router-dom'

import React from 'react'
import { icons } from '@config'

export default function Edit() {
  const { id } = useParams()
  const history = useHistory()
  const { data: tenant, fetch: fetchTenant } = useGet({ endpoint: `api/tenant/${id}` })
  var { register, post, setValue, getValues, error, loading, handleSubmit, watch } = useSubmit({
    endpoint: 'api/tenant',
    form: tenant,
    method: 'patch',
    success: () => {
      history.push(`/tenant/${id}`)
    }
  })

  const submit = handleSubmit(data => {
    data.organisation = data.organisation === 'true'
    post(data)
  })

  const { post: createCustomer, loading: creatingCustomer } = usePost({
    confirm: 'Är du säker på att du vill skapa en ny kund i integrationen? Om det redan finns en kund med samma person- eller organisationsnummer kommer den att kopplas till den här hyresgästen.',
    success: fetchTenant,
  })

  const handleAutogiroToggled = async (isChecked, nextValue) => {
    if (!nextValue) return true
    const shouldProceed = await notify.confirm("Jag är medveten om att autogiro endast fungerar om kunden har ett aktivt medgivande i Fortnox ")
    return shouldProceed
  }

  const integrations = React.useMemo(() => {
    if (!tenant) return []
    const seen = new Set()
    const result = []

    for (const ic of tenant.integration_customers || []) {
      result.push({
        id: ic.integration.id,
        name: ic.integration.nice_name,
        description: ic.integration.description,
        customerNumber: ic.customer_number,
        connected: true,
      })
      seen.add(ic.integration.id)
    }

    for (const lease of tenant.leases || []) {
      const integration = lease.entity?.company?.invoicingconfig?.integration
      if (integration && !seen.has(integration.id)) {
        result.push({
          id: integration.id,
          name: integration.nice_name,
          description: integration.description,
          customerNumber: null,
          connected: false,
        })
        seen.add(integration.id)
      }
    }

    return result
  }, [tenant])

  const hasConnectedFortnox = integrations.some(i => i.connected && i.name === 'Fortnox')

  return (
    <>
      <ViewHeader
        loading={!tenant}
        title='Hyresgäst'
        icon={icons.tenant}
        subtitle='Redigera'
        return={{ link: `#!/tenant/${id}`, title: getValues('name') }}
        buttons={[{ icon: 'fa-times', title: 'Avbryt', href: `#!/tenant/${id}`, position: 'right' }]}
      />
      {integrations.length > 0 && <Help>
        <b>Viktigt att veta när du uppdaterar hyresgästinformation</b>
        <br />
        <br />
        När du gör ändringar i hyresgästuppgifterna här i vårt system skriver vi samma ändringar till alla integrationer.
        <br />
        Ändringar som görs direkt i systemen synkroniseras däremot <b>inte</b> tillbaka hit.
      </Help>}
      <Card
    title="Redigera hyresgäst"
    icon="fas fa-edit"
        loading={!tenant}
        buttons={{
          footer: [<Submit onSubmit={submit} text='Uppdatera' sending={loading} />]
        }}>
        <input type='hidden' {...register('tenant_id')} value={id} />
        <div class='row mb-3'>
          <Select
            controls={{ register }}
            name='organisation'
            label='Kundtyp'
            error={error}
            values={[
              { key: 'Privatkund', value: false },
              { key: 'Företagskund', value: true }
            ]}
          />
        </div>
        <div class='row mb-3'>
          <Input controls={{ register }} name='name' options={{ required: true }} label='Namn' error={error} />
          <Input controls={{ register }} name='identifier' options={{ required: true }} label='Pers/Org-nummer' error={error} />
        </div>
        <div class='row mb-3'>
          <Input controls={{ register }} name='phone' options={{ required: true }} label='Telefonnummer' error={error} />
          <Input controls={{ register }} name='email' options={{ required: false }} label='E-post' error={error} />
        </div>
        <div class='row mb-3'>
          <Input
            controls={{ register }}
            name='billing_email'
            options={{ required: false }}
            label='E-post (Fakturering)'
            placeholder='Kontakt-e-post används för fakturor om inget annat anges'
            error={error}
          />
        </div>
        {hasConnectedFortnox &&
          <div class='form-group'>
            <label>
              Autogiro <Explination tip='Hur fungerar autogiro i Fortnox?' />
            </label>
            <br />
            <CheckBox error={error} controls={{ register, getValues, setValue, watch }} name='autogiro' options={{ required: false }} label='Autogiro' preChangeHook={handleAutogiroToggled} />
          </div>
        }
      </Card>
      {integrations.length > 0 && <Card
        title='Integrationer relaterade till hyresgäst'
        icon={icons.link}
        loading={!tenant}
      >
        <Table
          rows={integrations}
          functions={{
            customerNumber: i => i.connected ? i.customerNumber : '—',
            action: i => !i.connected
              ? <button class='btn btn-sm btn-primary' disabled={creatingCustomer} onClick={() => createCustomer({ endpoint: `api/tenant/${id}/create_customer?integration_id=${i.id}` })}>Skapa</button>
              : null
          }}
          keys={['name', 'description', '_this>function:customerNumber', '_this>function:action']}>
          <th>Integration</th>
          <th>Beskrivning</th>
          <th>Kundnummer</th>
          <th />
        </Table>
      </Card>}
    </>
  )
}
